/**
 * What a finger does.
 *
 * The rules, and there are only three, because a tool a homeowner opens once must not have
 * a mode to learn:
 *
 *   one finger on a cabinet   moves it along its run
 *   one finger on anything else   orbits the room
 *   two fingers   zoom and pan
 *
 * No gizmo. A gizmo is three arrows a few pixels wide and a fingertip is eleven millimetres,
 * so the arrows are either too small to hit or too big to see past. The run gives us the
 * axis for free, which is the whole reason the interaction can be this plain.
 *
 * Everything below is about making that one drag feel like moving an object rather than
 * editing a field: it lifts when you take it, its neighbours get out of the way while your
 * finger is still down, it pulls into place rather than jumping, and it says so with a tick.
 */
import { slide, offsets } from './layout.js'
import { chainOf } from './room.js'
import { MM } from './build.js'

const SNAP_MM = 45          // how close before a target claims the drag
const LIFT_MM = 14          // how far a picked-up cabinet rises
const EASE = 0.28           // how quickly things slide to where they belong

export function attach(kitchen, opts = {}) {
  const el = kitchen.canvas
  const haptic = opts.haptic ?? (() => {})
  const onCommit = opts.onCommit ?? (() => {})
  const onCross = opts.onCross ?? (() => {})
  const onHint = opts.onHint ?? (() => {})
  const onSelect = opts.onSelect ?? (() => {})

  let drag = null
  let orbiting = null
  let pinch = null
  let lastSnap = null

  // Where every cabinet WANTS to be. The scene eases toward these each frame rather than
  // being set from them, which is what turns a re-order into something that slides.
  const wanted = new Map()

  const pointers = new Map()

  // ── choosing what a touch is for ───────────────────────────────────────────────────

  el.addEventListener('pointerdown', (e) => {
    // Guarded: capture throws NotFoundError for a pointer the browser no longer considers
    // active, which happens on a cancelled touch and in anything driving this synthetically.
    // Losing capture costs a drag that ends early; an exception here costs the gesture
    // entirely, because nothing after it runs.
    try { el.setPointerCapture(e.pointerId) } catch { /* capture is a nicety */ }
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (pointers.size === 2) {
      // A second finger cancels whatever the first was doing and becomes a pinch. Better
      // than trying to keep dragging with one of two fingers, which nobody means.
      if (drag) release(true)
      orbiting = null
      const [a, b] = [...pointers.values()]
      pinch = {
        d: Math.hypot(a.x - b.x, a.y - b.y),
        radius: kitchen.orbit.radius,
        // The midpoint, so the same two fingers can PAN as well as zoom: the distance
        // between them is the zoom and where they are together is the pan.
        cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2,
        target: kitchen.target.clone(),
      }
      return
    }
    if (pointers.size > 2) return

    const hit = kitchen.at(e.clientX, e.clientY)
    if (hit && hit.userData.itemId) {
      const run = kitchen.plan.runs.find((r) => r.id === hit.userData.runId)
      if (!run) return
      const index = run.items.findIndex((i) => i.id === hit.userData.itemId)
      if (index < 0) return
      // Locked: a tap still opens it (to unlock), a drag does nothing but say so.
      if ((kitchen.plan.locks ?? []).some((l) => l.itemId === hit.userData.itemId)) {
        drag = { run, index, group: hit, locked: true, from: { x: e.clientX, y: e.clientY }, moved: false, chain: { legs: [run] }, grabbed: 0, before: run.items.slice() }
        return
      }

      drag = {
        run,
        index,
        // The rail this cabinet is on -- every leg of it, so the drag can go round a corner.
        chain: chainOf(kitchen.plan, run).chain,
        crossed: false,
        group: hit,
        // Where along the run the finger grabbed it, so the cabinet does not jump to have
        // its left edge under the fingertip.
        grabbed: kitchen.alongRun(run, e.clientX, e.clientY) - offsets(run.items)[index],
        before: run.items.slice(),
        moved: false,
      }
      hit.userData.lifted = true
      // Where the finger went down, so a release close to it can be told from a drag. A tap
      // is not a short drag: it is a different question, and answering it as "you moved this
      // cabinet nowhere" instead of "you asked about this cabinet" is what makes a tool feel
      // like it is ignoring you.
      drag.from = { x: e.clientX, y: e.clientY }
      kitchen.onPick(run.items[index], run)
      haptic()
    } else {
      orbiting = { x: e.clientX, y: e.clientY, theta: kitchen.orbit.theta, phi: kitchen.orbit.phi }
    }
  })

  el.addEventListener('pointermove', (e) => {
    if (!pointers.has(e.pointerId)) return
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (pinch && pointers.size === 2) {
      const [a, b] = [...pointers.values()]
      const d = Math.hypot(a.x - b.x, a.y - b.y)
      kitchen.orbit.radius = clamp(pinch.radius * (pinch.d / Math.max(d, 1)), 1.5, 30)
      /*
       * AND TWO FINGERS PAN.
       *
       * Two fingers only ever zoomed, so the only way to look at the far end of a long run
       * was to orbit round to it -- and orbiting past a wall puts you outside the room. The
       * midpoint of the two fingers moves the camera's TARGET across the screen plane, which
       * is what every map and every photo app does with two fingers and what a hand expects.
       * Scaled by the radius so a pan feels the same at any zoom, and clamped to the room's
       * own extent plus a metre so it cannot be flicked into empty space and lost.
       */
      kitchen.panBy((a.x + b.x) / 2 - pinch.cx, (a.y + b.y) / 2 - pinch.cy, pinch.target)
      kitchen.touched = true
      kitchen.place()
      return
    }

    if (drag) { move(e); return }

    if (orbiting) {
      kitchen.orbit.theta = orbiting.theta - (e.clientX - orbiting.x) * 0.006
      kitchen.orbit.phi = clamp(orbiting.phi - (e.clientY - orbiting.y) * 0.005, 0.25, 1.5)
      // Once it has been turned, it is theirs: a resize must not snap it back to ours.
      kitchen.touched = true
      kitchen.place()
    }
  })

  for (const type of ['pointerup', 'pointercancel']) {
    el.addEventListener(type, (e) => {
      pointers.delete(e.pointerId)
      if (pointers.size < 2) pinch = null
      if (drag) release(false, e)
      orbiting = null
    })
  }

  el.addEventListener('wheel', (e) => {
    e.preventDefault()
    kitchen.orbit.radius = clamp(kitchen.orbit.radius * (1 + Math.sign(e.deltaY) * 0.1), 1.5, 30)
    kitchen.touched = true
    kitchen.place()
  }, { passive: false })

  // ── the drag itself ────────────────────────────────────────────────────────────────

  function move(e) {
    if (drag.locked) {
      const slop = Math.hypot(e.clientX - drag.from.x, e.clientY - drag.from.y)
      if (slop > 12 && lastSnap !== 'locked') { lastSnap = 'locked'; onHint('Locked in place. Tap it to unlock.') }
      return
    }
    const item = drag.run.items[drag.index]

    /*
     * Round the corner.
     *
     * The finger is answered by whichever leg of the rail it is nearest. When that is a
     * different run from the one the cabinet is on, the cabinet moves across -- width out of
     * the new wall's neighbours, back to the old wall's -- and the drag carries on from
     * there with a fresh mesh, because the scene rebuilt. A wall that cannot make room says
     * so and the cabinet stays where it was, which is the same refusal add() gives.
     */
    const near = kitchen.alongChain(drag.chain, e.clientX, e.clientY, drag.grabbed, item.w)
    if (near && near.run !== drag.run) {
      if (!drag.crossed) { onCross(); drag.crossed = true }
      const r = kitchen.moveAcross(item.id, drag.run, near.run, near.x - near.run.from)
      if (!r.ok) { onHint(r.why); return }
      drag.run = r.run
      drag.index = r.index
      drag.moved = true
      const mesh = kitchen.meshOf(item.id)
      if (mesh) { drag.group = mesh; mesh.userData.lifted = true }
      for (const leg of drag.chain.legs) layout(leg, kitchen)
      haptic()
      onHint(r.said ?? 'Round the corner')
      lastSnap = 'corner'
      return
    }

    const { run } = drag
    const raw = kitchen.alongRun(run, e.clientX, e.clientY) - drag.grabbed
    const { x, snap } = magnet(raw, run, item, kitchen)

    // Re-order live, so the neighbours are already out of the way when you let go rather
    // than rearranging themselves afterwards.
    const next = slide(run.items, drag.index, x - run.from)
    const moved = next.findIndex((i) => i.id === item.id)
    if (moved !== drag.index) {
      run.items = next
      drag.index = moved
      drag.moved = true
      layout(run, kitchen)
      haptic()
    }

    // The dragged one follows the finger; everything else eases to where it belongs.
    drag.group.position.x = x * MM
    drag.group.position.y = LIFT_MM * MM

    if (snap !== lastSnap) {
      if (snap) haptic()
      lastSnap = snap
      onHint(snap)
    }
  }

  function release(cancelled, e) {
    if (drag.group) {
      drag.group.userData.lifted = false
      drag.group.position.y = 0
    }
    const item = drag.run.items[drag.index]

    // A release within a fingertip of where it started, having moved nothing, is a TAP.
    const slop = e && drag.from
      ? Math.hypot(e.clientX - drag.from.x, e.clientY - drag.from.y) : 999
    const tapped = !cancelled && !drag.moved && slop < 12

    // A drag that went round a corner was remembered as a whole-room step when it crossed;
    // one that stayed on its wall is remembered as that wall's arrangement, here.
    if (!cancelled && drag.moved && !drag.crossed) onCommit(drag.run, drag.before)
    layout(drag.run, kitchen)
    const run = drag.run
    drag = null
    lastSnap = null
    onHint(null)
    if (tapped) onSelect(item, run)
  }

  /**
   * Pull toward the places a cabinet wants to be.
   *
   * Wall ends first, because a run that does not reach the wall is the commonest thing a
   * homeowner gets wrong and the easiest to help with. Then neighbours, then the 32mm grid
   * the factory is set up for. Nearest wins, and only within `SNAP_MM` -- outside that the
   * finger is in charge, which is what keeps it from feeling sticky.
   */
  function magnet(x, run, item, kitchen) {
    const targets = [
      { x: run.from, why: 'against the wall' },
      { x: run.to - item.w, why: 'against the far wall' },
    ]

    let at = 0
    for (const other of run.items) {
      if (other.id !== item.id) {
        targets.push({ x: run.from + at, why: `beside the ${label(other, kitchen)}` })
        targets.push({ x: run.from + at - item.w, why: `beside the ${label(other, kitchen)}` })
      }
      at += other.w
    }

    /*
     * A named target beats the grid, always.
     *
     * The 32mm grid is never more than 16mm away, so putting it in the same contest as the
     * wall and the neighbours meant it won essentially every time -- and it is the one target
     * with nothing to say, so the tool snapped constantly and explained itself never. The
     * grid is where a cabinet lands when nothing better wants it, not a competitor.
     */
    let best = null
    for (const t of targets) {
      const d = Math.abs(t.x - x)
      if (d <= SNAP_MM && (!best || d < Math.abs(best.x - x))) best = t
    }
    if (!best) best = { x: Math.round((x - run.from) / 32) * 32 + run.from, why: null }

    const clamped = clamp(best.x, run.from, run.to - item.w)
    return { x: clamped, snap: best.why }
  }

  function label(item, kitchen) {
    if (item.filler) return 'filler'
    return kitchen.maker.cabinets.find((c) => c.id === item.specId)?.name ?? 'cabinet'
  }

  /** Put every cabinet in a run where the arithmetic says it goes. */
  function layout(run, kitchen) {
    const group = kitchen.cabinets.children.find((g) => g.userData.runId === run.id)
    if (!group) return
    const at = offsets(run.items)
    run.items.forEach((item, i) => {
      const mesh = group.children.find((c) => c.userData.itemId === item.id)
      if (!mesh) return
      mesh.userData.index = i
      wanted.set(mesh, (run.from + at[i]) * MM)
    })
  }

  /** Ease everything toward where it belongs. One frame's worth. */
  function settle() {
    for (const [mesh, x] of wanted) {
      if (mesh.userData.lifted) continue
      const d = x - mesh.position.x
      if (Math.abs(d) < 0.0002) { mesh.position.x = x; wanted.delete(mesh); continue }
      mesh.position.x += d * EASE
    }
    requestAnimationFrame(settle)
  }
  settle()

  return { detach() { /* the canvas outlives nothing here; kept for symmetry */ } }
}

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
